import { createStore, get, set, update } from "idb-keyval";
import type { GroceryItem, GroceryList } from "../types";

// Whole-collection read/write via a handful of keys in one IndexedDB store.
// At household-grocery-list scale (a few lists, at most a few hundred items)
// this is simpler and just as reliable as per-record object stores with
// indexes, and it keeps the offline sync logic in one readable place.

const store = createStore("groceries-db", "kv");

export type OutboxOp =
  | "list.create"
  | "list.update"
  | "list.delete"
  | "item.create"
  | "item.update"
  | "item.delete"
  | "list.clearChecked";

export interface OutboxEntry {
  seq: number;
  op: OutboxOp;
  entityId: string;
  listId?: string;
  payload: Record<string, unknown>;
}

interface Meta {
  lastSyncAt: number;
  outboxSeq: number;
}

const KEYS = {
  lists: "lists",
  items: "items",
  outbox: "outbox",
  meta: "meta",
} as const;

export async function loadLists(): Promise<GroceryList[]> {
  return (await get<GroceryList[]>(KEYS.lists, store)) ?? [];
}

export async function saveLists(lists: GroceryList[]): Promise<void> {
  await set(KEYS.lists, lists, store);
}

export async function loadItems(): Promise<GroceryItem[]> {
  return (await get<GroceryItem[]>(KEYS.items, store)) ?? [];
}

export async function saveItems(items: GroceryItem[]): Promise<void> {
  await set(KEYS.items, items, store);
}

export async function loadOutbox(): Promise<OutboxEntry[]> {
  return (await get<OutboxEntry[]>(KEYS.outbox, store)) ?? [];
}

export async function saveOutbox(entries: OutboxEntry[]): Promise<void> {
  await set(KEYS.outbox, entries, store);
}

export async function enqueue(op: OutboxOp, entityId: string, payload: Record<string, unknown>, listId?: string): Promise<void> {
  const meta = await getMeta();
  const seq = meta.outboxSeq + 1;
  await setMeta({ ...meta, outboxSeq: seq });
  const entry: OutboxEntry = { seq, op, entityId, payload, ...(listId !== undefined ? { listId } : {}) };
  await update<OutboxEntry[]>(KEYS.outbox, (entries) => [...(entries ?? []), entry], store);
}

export async function getMeta(): Promise<Meta> {
  return (await get<Meta>(KEYS.meta, store)) ?? { lastSyncAt: 0, outboxSeq: 0 };
}

export async function setMeta(meta: Meta): Promise<void> {
  await set(KEYS.meta, meta, store);
}
